import { type VendorCatalogProduct } from '@choco-admin/types';
import { Button, Chip, Image, Spinner } from '@heroui/react';
import React from 'react';
import { FiPackage } from 'react-icons/fi';

import { AlertCard } from '../../../../../../../components/AlertCard';
import { BaseModal } from '../../../../../../../components/BaseModal';
import { useVendorItemUnitDetail } from '../hooks/useVendorItemUnitDetail';
import { stockStatusChip } from '../utils/catalogStatusChip';
import { formatVendorCatalogPrice } from '../utils/vendorCatalogMapper';

interface CatalogProductDetailModalProps {
  /** The selected product row, or null when the modal is closed. */
  product: VendorCatalogProduct | null;
  onClose: () => void;
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs uppercase tracking-wide text-default-400">
      {label}
    </span>
    <span className="text-sm text-foreground">{value}</span>
  </div>
);

const MonoOrDash: React.FC<{ value: string | null }> = ({ value }) =>
  value ? <span className="font-mono text-xs">{value}</span> : <>—</>;

export const CatalogProductDetailModal: React.FC<
  CatalogProductDetailModalProps
> = ({ product, onClose }) => {
  const { detail, isLoading, errorMessage } = useVendorItemUnitDetail(
    product?.id ?? null
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-10">
          <Spinner label="Loading product details..." />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <AlertCard
          type="error"
          title="Could not load product details"
          message={errorMessage}
        />
      );
    }

    if (!detail) {
      return (
        <AlertCard
          type="info"
          title="Product not found"
          message="This product could not be found in the distributor's catalog."
        />
      );
    }

    const stock = stockStatusChip[detail.stockStatus];
    const factorLabel =
      detail.factor && detail.factor.denominator
        ? `${detail.factor.numerator} : ${detail.factor.denominator}`
        : null;
    const baseUnitValue = detail.baseUnitName
      ? `${detail.baseUnitName}${factorLabel ? ` (${factorLabel})` : ''}`
      : '—';
    const leadTimeValue =
      detail.leadTimeDays !== null
        ? `${detail.leadTimeDays} day${detail.leadTimeDays === 1 ? '' : 's'}`
        : '—';

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row">
          <Image
            src={detail.imageUrls[0] ?? undefined}
            width={140}
            height={140}
            radius="lg"
            alt={detail.name}
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E"
            className="flex-shrink-0 border border-default-200 bg-default-100 object-cover"
          />
          <div className="flex flex-1 flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {stock && (
                <Chip size="sm" variant="flat" color={stock.color}>
                  {stock.label}
                </Chip>
              )}
              <Chip
                size="sm"
                variant="flat"
                color={detail.isEnabled ? 'success' : 'default'}
              >
                {detail.isEnabled ? 'Orderable' : 'Not orderable'}
              </Chip>
              {detail.isHiddenByDefault && (
                <Chip size="sm" variant="flat" color="default">
                  Hidden by default
                </Chip>
              )}
            </div>
            {detail.description ? (
              <p className="text-sm text-default-600">{detail.description}</p>
            ) : (
              <p className="text-sm italic text-default-400">No description</p>
            )}
            {detail.categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {detail.categories.map((category) => (
                  <Chip
                    key={category.id}
                    size="sm"
                    variant="bordered"
                    startContent={<FiPackage size={12} />}
                  >
                    {category.name}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="Unit" value={detail.unitName || '—'} />
          <Field
            label="Product code"
            value={<MonoOrDash value={detail.productCode} />}
          />
          <Field
            label="Matching code"
            value={<MonoOrDash value={detail.matchingCode} />}
          />
          <Field
            label="Legacy id"
            value={<MonoOrDash value={detail.legacyId} />}
          />
          <Field label="Packaging" value={detail.packagingDescription || '—'} />
          <Field label="Brand" value={detail.brand || '—'} />
          <Field label="Origin" value={detail.originCountryCode || '—'} />
          <Field label="Base unit" value={baseUnitValue} />
          <Field label="Lead time" value={leadTimeValue} />
          <Field label="Cut-off time" value={detail.cutOffTime || '—'} />
        </div>

        <div>
          <h4 className="mb-2 text-sm font-semibold text-foreground">Prices</h4>
          <div className="grid grid-cols-3 gap-4">
            <Field
              label="Catalog"
              value={formatVendorCatalogPrice(detail.catalogPrice)}
            />
            <Field
              label="Base"
              value={formatVendorCatalogPrice(detail.basePrice)}
            />
            <Field
              label="Display"
              value={formatVendorCatalogPrice(detail.displayPrice)}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <BaseModal
      isOpen={product !== null}
      onClose={onClose}
      size="2xl"
      title={product?.name ?? 'Product'}
      subtitle={product?.unit || undefined}
      scrollBehavior="inside"
      footer={
        <div className="flex w-full items-center justify-between">
          <span className="text-xs text-default-400">
            Read-only — catalog content is managed in Retool.
          </span>
          <Button variant="flat" onPress={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {renderBody()}
    </BaseModal>
  );
};
